import { Navigate, Route, useParams, useSearchParams } from "react-router-dom";
import {
  lazy,
  Suspense,
  type LazyExoticComponent,
  type ComponentType,
} from "react";
import { ProtectedLayout } from "@/components/ProtectedLayout";
import { StaffPermissionGuard } from "@/components/auth/StaffPermissionGuard";
import { CourseFacilityScopeGuard } from "@/components/common/CourseFacilityScopeGuard";
import ChatPanelLoading from "@/pages/(authenticated)/chat/loading";

// admission
const AdmissionCode = lazy(
  () => import("@/pages/(authenticated)/admission/code/page")
);
const AdmissionRequests = lazy(
  () => import("@/pages/(authenticated)/admission/requests/page")
);
const AdmissionTerms = lazy(
  () => import("@/pages/(authenticated)/admission/terms/page")
);

// analytics
const AnalyticsCustomers = lazy(
  () => import("@/pages/(authenticated)/analytics/customers/page")
);
const AnalyticsManagement = lazy(
  () => import("@/pages/(authenticated)/analytics/management/page")
);

// billing
const BillingContract = lazy(
  () => import("@/pages/(authenticated)/billing/contract/page")
);
const BillingDirectDebit = lazy(
  () => import("@/pages/(authenticated)/billing/direct-debit/page")
);
const BillingHistory = lazy(
  () => import("@/pages/(authenticated)/billing/history/page")
);
const BillingPaymentSetup = lazy(
  () => import("@/pages/(authenticated)/billing/payment-setup/page")
);
const BillingSubscriptions = lazy(
  () => import("@/pages/(authenticated)/billing/subscriptions/page")
);
const BillingTuitionPlans = lazy(
  () => import("@/pages/(authenticated)/billing/tuition-plans/page")
);
const BillingDiscountRules = lazy(
  () => import("@/pages/(authenticated)/billing/discount-rules/page")
);
const BillingDiscountApplications = lazy(
  () => import("@/pages/(authenticated)/billing/discount-applications/page")
);
const BillingDiscountRuleEditor = lazy(
  () => import("@/pages/(authenticated)/billing/discount-rules/editor/page")
);
const BillingTicketProducts = lazy(
  () => import("@/pages/(authenticated)/billing/ticket-products/page")
);
const BillingTicketProductEditor = lazy(
  () => import("@/pages/(authenticated)/billing/ticket-products/new/page")
);
const BillingCoupons = lazy(
  () => import("@/pages/(authenticated)/billing/coupons/page")
);
const BillingCouponEditor = lazy(
  () => import("@/pages/(authenticated)/billing/coupons/new/page")
);
const BillingCouponActivity = lazy(
  () => import("@/pages/(authenticated)/billing/coupon-activity/page")
);
const SettingsTicketing = lazy(
  () => import("@/pages/(authenticated)/settings/ticketing/page")
);
const TicketReception = lazy(
  () =>
    import("@/pages/(authenticated)/facilities/[id]/ticket-reservations/page")
);

// operator
const OperatorTenantPayments = lazy(
  () => import("@/pages/(authenticated)/operator/tenant-payments/page")
);
const OperatorDataReconciliation = lazy(
  () => import("@/pages/(authenticated)/operator/data-reconciliation/page")
);
const OperatorDataMigrations = lazy(
  () => import("@/pages/(authenticated)/operator/data-migrations/page")
);
const OperatorAppStatus = lazy(
  () => import("@/pages/(authenticated)/operator/app-status/page")
);
const OperatorPlatformAccesses = lazy(
  () => import("@/pages/(authenticated)/operator/platform-accesses/page")
);

// organization
const OrganizationBranding = lazy(
  () => import("@/pages/(authenticated)/organization/branding/page")
);
const OrganizationPayoutAccount = lazy(
  () => import("@/pages/(authenticated)/organization/payout-account/page")
);
const OrganizationMembershipPolicy = lazy(
  () => import("@/pages/(authenticated)/organization/membership-policy/page")
);
const OrganizationMemberManagementMode = lazy(
  () =>
    import("@/pages/(authenticated)/organization/member-management-mode/page")
);
const SuspensionRulesPage = lazy(
  () =>
    import(
      "@/pages/(authenticated)/organization/membership-policy/suspension-rules/page"
    )
);
const OrganizationReschedulePolicy = lazy(
  () => import("@/pages/(authenticated)/organization/reschedule-policy/page")
);
const OrganizationAttendanceSetting = lazy(
  () => import("@/pages/(authenticated)/organization/attendance-setting/page")
);
const OrganizationChatSupportPolicy = lazy(
  () => import("@/pages/(authenticated)/organization/chat-support-policy/page")
);

// chat
const ChatLayout = lazy(() => import("@/pages/(authenticated)/chat/layout"));
const Chat = lazy(() => import("@/pages/(authenticated)/chat/page"));
const ChatRoom = lazy(
  () => import("@/pages/(authenticated)/chat/[roomId]/page")
);

// coaches
const Coaches = lazy(() => import("@/pages/(authenticated)/coaches/page"));
const CoachCreate = lazy(
  () => import("@/pages/(authenticated)/coaches/create/page")
);

// courses
const Courses = lazy(() => import("@/pages/(authenticated)/courses/page"));
const CourseCreate = lazy(
  () => import("@/pages/(authenticated)/courses/create/page")
);
const CourseDetail = lazy(
  () => import("@/pages/(authenticated)/courses/[id]/page")
);
const CourseEdit = lazy(
  () => import("@/pages/(authenticated)/courses/[id]/edit/page")
);
const CourseSessions = lazy(
  () => import("@/pages/(authenticated)/courses/[id]/sessions/page")
);
const CourseScheduleChange = lazy(
  () => import("@/pages/(authenticated)/courses/[id]/schedule-change/page")
);
const CourseSessionCreate = lazy(
  () => import("@/pages/(authenticated)/courses/[id]/sessions/create/page")
);
const CourseSessionDetail = lazy(
  () => import("@/pages/(authenticated)/courses/[id]/sessions/[sessionId]/page")
);
const LessonOccurrenceDetail = lazy(
  () =>
    import(
      "@/pages/(authenticated)/courses/[id]/sessions/[sessionId]/lessons/[date]/page"
    )
);
const CourseSessionEdit = lazy(
  () =>
    import(
      "@/pages/(authenticated)/courses/[id]/sessions/[sessionId]/edit/page"
    )
);

// customers
const CustomerMembers = lazy(
  () => import("@/pages/(authenticated)/customers/parents/page")
);
const CustomerMembersInvite = lazy(
  () => import("@/pages/(authenticated)/customers/parents/invite/page")
);
const CustomerMemberIdentityReview = lazy(
  () => import("@/pages/(authenticated)/customers/parents/identity-review/page")
);
const CustomerMemberDetail = lazy(
  () => import("@/pages/(authenticated)/customers/parents/[id]/page")
);
const CustomerMemberEdit = lazy(
  () => import("@/pages/(authenticated)/customers/parents/[id]/edit/page")
);
const CustomerChildren = lazy(
  () => import("@/pages/(authenticated)/customers/children/page")
);
const CustomerChildCreate = lazy(
  () => import("@/pages/(authenticated)/customers/children/create/page")
);
const CustomerChildIdentityReview = lazy(
  () =>
    import("@/pages/(authenticated)/customers/children/identity-review/page")
);
const CustomerChildDetail = lazy(
  () => import("@/pages/(authenticated)/customers/children/[id]/page")
);
const CustomerChildEdit = lazy(
  () => import("@/pages/(authenticated)/customers/children/[id]/edit/page")
);
const CustomerImport = lazy(
  () => import("@/pages/(authenticated)/customers/import/page")
);

// dashboard
const Dashboard = lazy(() => import("@/pages/(authenticated)/dashboard/page"));

// events
const Events = lazy(() => import("@/pages/(authenticated)/events/page"));
const EventCreate = lazy(
  () => import("@/pages/(authenticated)/events/create/page")
);
const EventDetail = lazy(
  () => import("@/pages/(authenticated)/events/[id]/page")
);
const EventEdit = lazy(
  () => import("@/pages/(authenticated)/events/[id]/edit/page")
);
const EventParticipants = lazy(
  () => import("@/pages/(authenticated)/events/[id]/participants/page")
);

// facilities
const Facilities = lazy(
  () => import("@/pages/(authenticated)/facilities/page")
);
const FacilityCreate = lazy(
  () => import("@/pages/(authenticated)/facilities/create/page")
);
const FacilityDetail = lazy(
  () => import("@/pages/(authenticated)/facilities/[id]/page")
);
const FacilityEdit = lazy(
  () => import("@/pages/(authenticated)/facilities/[id]/edit/page")
);
const FacilityClosedDates = lazy(
  () => import("@/pages/(authenticated)/facilities/[id]/closed-dates/page")
);
const FacilityMembershipPolicy = lazy(
  () => import("@/pages/(authenticated)/facilities/[id]/membership-policy/page")
);
const FacilityMemberManagementMode = lazy(
  () =>
    import(
      "@/pages/(authenticated)/facilities/[id]/member-management-mode/page"
    )
);
const FacilityReschedulePolicy = lazy(
  () => import("@/pages/(authenticated)/facilities/[id]/reschedule-policy/page")
);
const FacilityAttendanceSetting = lazy(
  () =>
    import("@/pages/(authenticated)/facilities/[id]/attendance-setting/page")
);
const FacilityChatSupportPolicy = lazy(
  () =>
    import("@/pages/(authenticated)/facilities/[id]/chat-support-policy/page")
);
const FacilityInstructors = lazy(
  () => import("@/pages/(authenticated)/facilities/[id]/instructors/page")
);
const FacilityInstructorCreate = lazy(
  () =>
    import("@/pages/(authenticated)/facilities/[id]/instructors/create/page")
);
const FacilityInstructorEdit = lazy(
  () =>
    import(
      "@/pages/(authenticated)/facilities/[id]/instructors/[instructorId]/edit/page"
    )
);
const FacilityRooms = lazy(
  () => import("@/pages/(authenticated)/facilities/[id]/rooms/page")
);
const FacilityRoomCreate = lazy(
  () => import("@/pages/(authenticated)/facilities/[id]/rooms/create/page")
);
const FacilityRoomEdit = lazy(
  () =>
    import("@/pages/(authenticated)/facilities/[id]/rooms/[roomId]/edit/page")
);

// faqs
const Faqs = lazy(() => import("@/pages/(authenticated)/faqs/page"));
const FaqCreate = lazy(
  () => import("@/pages/(authenticated)/faqs/create/page")
);
const FaqEditPage = lazy(
  () => import("@/pages/(authenticated)/faqs/[id]/edit/page")
);

// invitations
const Invitations = lazy(
  () => import("@/pages/(authenticated)/invitations/page")
);
const InvitationCreate = lazy(
  () => import("@/pages/(authenticated)/invitations/create/page")
);
const InvitationsAnalytics = lazy(
  () => import("@/pages/(authenticated)/invitations/analytics/page")
);

// notifications
const Notifications = lazy(
  () => import("@/pages/(authenticated)/notifications/page")
);
const NotificationInbox = lazy(
  () => import("@/pages/(authenticated)/notifications/inbox/page")
);
const NotificationCreate = lazy(
  () => import("@/pages/(authenticated)/notifications/create/page")
);
const NotificationDetail = lazy(
  () => import("@/pages/(authenticated)/notifications/[id]/page")
);
const NotificationEdit = lazy(
  () => import("@/pages/(authenticated)/notifications/[id]/edit/page")
);
const NotificationSegments = lazy(
  () => import("@/pages/(authenticated)/notifications/segments/page")
);
const NotificationSegmentCreate = lazy(
  () => import("@/pages/(authenticated)/notifications/segments/create/page")
);
const NotificationSegmentDetail = lazy(
  () => import("@/pages/(authenticated)/notifications/segments/[id]/page")
);
const NotificationSegmentEdit = lazy(
  () => import("@/pages/(authenticated)/notifications/segments/[id]/edit/page")
);

// attendance-reports
const AttendanceReports = lazy(
  () => import("@/pages/(authenticated)/attendance-reports/page")
);

// reschedule-requests
const MembershipRequests = lazy(
  () => import("@/pages/(authenticated)/membership-requests/page")
);
const CourseEnrollmentRequests = lazy(
  () => import("@/pages/(authenticated)/course-enrollment-requests/page")
);
const RescheduleRequests = lazy(
  () => import("@/pages/(authenticated)/reschedule-requests/page")
);

// settings
const SettingsAccount = lazy(
  () => import("@/pages/(authenticated)/settings/account/page")
);
const SettingsDataImport = lazy(
  () => import("@/pages/(authenticated)/settings/data-import/page")
);

// staff
const Staff = lazy(() => import("@/pages/(authenticated)/staff/page"));
const StaffCreate = lazy(
  () => import("@/pages/(authenticated)/staff/create/page")
);
const StaffEdit = lazy(
  () => import("@/pages/(authenticated)/staff/[id]/edit/page")
);
const StaffShiftsCreate = lazy(
  () => import("@/pages/(authenticated)/staff/shifts/create/page")
);
const StaffNotificationSettings = lazy(
  () => import("@/pages/(authenticated)/staff/notification-settings/page")
);

// trial-applications
const TrialApplications = lazy(
  () => import("@/pages/(authenticated)/trial-applications/page")
);

// trial-settings
const TrialSettings = lazy(
  () => import("@/pages/(authenticated)/trial-settings/page")
);

// trial-form-templates
const TrialFormTemplates = lazy(
  () => import("@/pages/(authenticated)/trial-form-templates/page")
);
const TrialFormTemplateCreate = lazy(
  () => import("@/pages/(authenticated)/trial-form-templates/create/page")
);
const TrialFormTemplateEdit = lazy(
  () => import("@/pages/(authenticated)/trial-form-templates/[id]/edit/page")
);

// products
const Products = lazy(() => import("@/pages/(authenticated)/products/page"));
const ProductNew = lazy(
  () => import("@/pages/(authenticated)/products/new/page")
);
const ProductDetail = lazy(
  () => import("@/pages/(authenticated)/products/[productId]/page")
);
const ProductEdit = lazy(
  () => import("@/pages/(authenticated)/products/[productId]/edit/page")
);
const ProductInventory = lazy(
  () => import("@/pages/(authenticated)/products/inventory/page")
);

// surveys
const Surveys = lazy(() => import("@/pages/(authenticated)/surveys/page"));
const SurveyCreate = lazy(
  () => import("@/pages/(authenticated)/surveys/create/page")
);
const SurveyDetail = lazy(
  () => import("@/pages/(authenticated)/surveys/[id]/page")
);
const SurveyEdit = lazy(
  () => import("@/pages/(authenticated)/surveys/[id]/edit/page")
);

// inquiries
const Inquiries = lazy(() => import("@/pages/(authenticated)/inquiries/page"));

// grade-programs
const GradePrograms = lazy(
  () => import("@/pages/(authenticated)/grade-programs/page")
);
const GradeTests = lazy(
  () => import("@/pages/(authenticated)/grade-tests/page")
);
const VideoMaterials = lazy(
  () => import("@/pages/(authenticated)/video-materials/page")
);
const LearningCatalog = lazy(
  () => import("@/pages/(authenticated)/learning-catalog/page")
);
const GradeRankDetail = lazy(
  () =>
    import(
      "@/pages/(authenticated)/grade-programs/[programId]/ranks/[rankId]/page"
    )
);
const GradeRankEdit = lazy(
  () =>
    import(
      "@/pages/(authenticated)/grade-programs/[programId]/ranks/[rankId]/edit/page"
    )
);

// orders
const Orders = lazy(() => import("@/pages/(authenticated)/orders/page"));
const OrderDetail = lazy(
  () => import("@/pages/(authenticated)/orders/[id]/page")
);

const Loading = () => <div style={{ padding: 24 }}>Loading...</div>;

// ProtectedLayout は親 Route の element として 1 度だけ mount される (FacilityProvider/SidebarProvider 等の Provider 群を維持するため)。
// 個々の page は ProtectedLayout 配下の <Outlet /> 位置に Suspense でラップして配置する。
// `any` は使わない (CLAUDE.md ルール)。
const wrap = (C: LazyExoticComponent<ComponentType<unknown>>) => (
  <Suspense fallback={<Loading />}>
    <C />
  </Suspense>
);

const wrapCourseScoped = (C: LazyExoticComponent<ComponentType<unknown>>) => (
  <CourseFacilityScopeGuard>{wrap(C)}</CourseFacilityScopeGuard>
);

const wrapCourseManaged = (C: LazyExoticComponent<ComponentType<unknown>>) => (
  <StaffPermissionGuard permission="course.manage">
    {wrapCourseScoped(C)}
  </StaffPermissionGuard>
);

const wrapChatPanel = (C: LazyExoticComponent<ComponentType<unknown>>) => (
  <Suspense fallback={<ChatPanelLoading />}>
    <C />
  </Suspense>
);

function FaqEditAdapter() {
  const params = useParams<{ id: string }>();
  return (
    <Suspense fallback={<Loading />}>
      <FaqEditPage params={{ id: params.id ?? "" }} />
    </Suspense>
  );
}

function BookingRedirectAdapter() {
  const [searchParams] = useSearchParams();
  const eventId = searchParams.get("eventId");
  return <Navigate to={eventId ? `/events/${eventId}` : "/events"} replace />;
}

function BillingHistoryRedirectAdapter({
  status,
  paymentType,
}: {
  status?: string;
  paymentType?: string;
}) {
  const [searchParams] = useSearchParams();
  const nextParams = new URLSearchParams(searchParams);
  if (status) nextParams.set("status", status);
  if (paymentType) nextParams.set("paymentType", paymentType);
  const query = nextParams.toString();
  return (
    <Navigate to={`/billing/history${query ? `?${query}` : ""}`} replace />
  );
}

function LegacyMemberRouteRedirect({
  destination,
}: {
  destination: "list" | "invite" | "identity-review" | "detail" | "edit";
}) {
  const params = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const id = params.id ? `/${encodeURIComponent(params.id)}` : "";
  const pathname =
    destination === "invite"
      ? "/customers/members/invite"
      : destination === "identity-review"
        ? "/customers/members/identity-review"
        : destination === "detail"
          ? `/customers/members${id}`
          : destination === "edit"
            ? `/customers/members${id}/edit`
            : "/customers/members";
  const query = searchParams.toString();
  return <Navigate to={`${pathname}${query ? `?${query}` : ""}`} replace />;
}

// 教室詳細は一覧内モーダルで表示するため、既存の詳細URLからも
// 対象教室を指定して一覧へ戻す。編集画面へは詳細内ボタンから進む。
function RoomDetailRedirectAdapter() {
  const params = useParams<{ id: string; roomId: string }>();
  return (
    <Navigate
      to={`/facilities/${params.id}/rooms?roomId=${params.roomId}`}
      replace
    />
  );
}

// 施設別コース管理は統合版へ一本化したため、既存のブックマークや
// 施設一覧からの古いURLは施設スコープを維持したまま統合版へ寄せる。
function FacilityCourseRedirectAdapter({
  destination,
}: {
  destination: "list" | "create" | "edit";
}) {
  const params = useParams<{ id: string; courseId: string }>();
  const facilityId = params.id ?? "";
  const query = new URLSearchParams({
    facilityId,
    workspaceFacility: facilityId,
  }).toString();
  const pathname =
    destination === "create"
      ? "/courses/create"
      : destination === "edit" && params.courseId
        ? `/courses/${params.courseId}/edit`
        : "/courses";
  return <Navigate to={`${pathname}?${query}`} replace />;
}

export const protectedRoutes = (
  <Route element={<ProtectedLayout />}>
    {/* admission */}
    <Route
      path="/admission/code"
      element={
        <StaffPermissionGuard permission="member.manage">
          {wrap(AdmissionCode)}
        </StaffPermissionGuard>
      }
    />
    <Route
      path="/admission/requests"
      element={
        <StaffPermissionGuard permission="member.manage">
          {wrap(AdmissionRequests)}
        </StaffPermissionGuard>
      }
    />
    <Route
      path="/admission/terms"
      element={
        <StaffPermissionGuard
          permission="role.manage"
          requireGlobalFacilityScope
        >
          {wrap(AdmissionTerms)}
        </StaffPermissionGuard>
      }
    />

    {/* analytics */}
    <Route
      path="/analytics"
      element={<Navigate to="/analytics/management" replace />}
    />
    <Route path="/analytics/management" element={wrap(AnalyticsManagement)} />
    <Route path="/analytics/customers" element={wrap(AnalyticsCustomers)} />
    <Route
      path="/analytics/events"
      element={<Navigate to="/analytics/customers?view=events" replace />}
    />
    {/* 売上分析は経営ダッシュボードと重複していたため統合（旧URLはリダイレクト） */}
    <Route
      path="/analytics/revenue"
      element={<Navigate to="/analytics/management" replace />}
    />
    <Route
      path="/analytics/bookings"
      element={<Navigate to="/dashboard" replace />}
    />
    <Route
      path="/analytics/staff"
      element={<Navigate to="/dashboard" replace />}
    />

    {/* billing */}
    <Route
      path="/billing/contract"
      element={
        <StaffPermissionGuard permission="billing.manage">
          {wrap(BillingContract)}
        </StaffPermissionGuard>
      }
    />
    <Route
      path="/billing/direct-debit"
      element={
        <StaffPermissionGuard
          permissions={["billing.manage", "bulk_operation.execute"]}
          requireGlobalFacilityScope
        >
          {wrap(BillingDirectDebit)}
        </StaffPermissionGuard>
      }
    />
    <Route
      path="/billing/history"
      element={
        <StaffPermissionGuard permission="billing.manage">
          {wrap(BillingHistory)}
        </StaffPermissionGuard>
      }
    />
    <Route
      path="/billing/one-time-charges"
      element={
        <StaffPermissionGuard permission="billing.manage">
          <BillingHistoryRedirectAdapter paymentType="ONE_TIME" />
        </StaffPermissionGuard>
      }
    />
    <Route
      path="/billing/overdue"
      element={
        <StaffPermissionGuard permission="billing.manage">
          <BillingHistoryRedirectAdapter status="UNPAID" />
        </StaffPermissionGuard>
      }
    />
    <Route
      path="/billing/subscriptions"
      element={
        <StaffPermissionGuard permission="billing.manage">
          {wrap(BillingSubscriptions)}
        </StaffPermissionGuard>
      }
    />
    <Route
      path="/billing/payment-setup"
      element={
        <StaffPermissionGuard
          permission="billing.manage"
          requireGlobalFacilityScope
        >
          {wrap(BillingPaymentSetup)}
        </StaffPermissionGuard>
      }
    />
    <Route
      path="/billing/tuition-plans"
      element={
        <StaffPermissionGuard permission="billing.manage">
          <Navigate to="/billing/pricing" replace />
        </StaffPermissionGuard>
      }
    />
    <Route
      path="/billing/pricing"
      element={
        <StaffPermissionGuard permission="billing.manage">
          {wrap(BillingTuitionPlans)}
        </StaffPermissionGuard>
      }
    />
    <Route
      path="/billing/discount-rules"
      element={
        <StaffPermissionGuard
          permission="billing.manage"
          requireGlobalFacilityScope
        >
          {wrap(BillingDiscountRules)}
        </StaffPermissionGuard>
      }
    />
    <Route
      path="/billing/discount-applications"
      element={
        <StaffPermissionGuard
          permission="billing.manage"
          requireGlobalFacilityScope
        >
          {wrap(BillingDiscountApplications)}
        </StaffPermissionGuard>
      }
    />
    <Route
      path="/billing/discount-rules/new"
      element={
        <StaffPermissionGuard
          permission="billing.manage"
          requireGlobalFacilityScope
        >
          {wrap(BillingDiscountRuleEditor)}
        </StaffPermissionGuard>
      }
    />
    <Route
      path="/billing/discount-rules/:ruleId/edit"
      element={
        <StaffPermissionGuard
          permission="billing.manage"
          requireGlobalFacilityScope
        >
          {wrap(BillingDiscountRuleEditor)}
        </StaffPermissionGuard>
      }
    />
    <Route
      path="/billing/ticket-products"
      element={
        <StaffPermissionGuard
          permission="billing.manage"
          requireGlobalFacilityScope
        >
          {wrap(BillingTicketProducts)}
        </StaffPermissionGuard>
      }
    />
    <Route
      path="/billing/ticket-products/new"
      element={
        <StaffPermissionGuard
          permission="billing.manage"
          requireGlobalFacilityScope
        >
          {wrap(BillingTicketProductEditor)}
        </StaffPermissionGuard>
      }
    />
    <Route
      path="/billing/ticket-products/:ticketProductId/edit"
      element={
        <StaffPermissionGuard
          permission="billing.manage"
          requireGlobalFacilityScope
        >
          {wrap(BillingTicketProductEditor)}
        </StaffPermissionGuard>
      }
    />
    <Route
      path="/billing/coupons"
      element={
        <StaffPermissionGuard
          permission="billing.manage"
          requireGlobalFacilityScope
        >
          {wrap(BillingCoupons)}
        </StaffPermissionGuard>
      }
    />
    <Route
      path="/billing/coupons/new"
      element={
        <StaffPermissionGuard
          permission="billing.manage"
          requireGlobalFacilityScope
        >
          {wrap(BillingCouponEditor)}
        </StaffPermissionGuard>
      }
    />
    <Route
      path="/billing/coupons/:couponId/edit"
      element={
        <StaffPermissionGuard
          permission="billing.manage"
          requireGlobalFacilityScope
        >
          {wrap(BillingCouponEditor)}
        </StaffPermissionGuard>
      }
    />
    <Route
      path="/billing/coupon-activity"
      element={
        <StaffPermissionGuard
          permission="billing.manage"
          requireGlobalFacilityScope
        >
          {wrap(BillingCouponActivity)}
        </StaffPermissionGuard>
      }
    />
    <Route
      path="/settings/ticketing"
      element={
        <StaffPermissionGuard
          permission="billing.manage"
          requireGlobalFacilityScope
        >
          {wrap(SettingsTicketing)}
        </StaffPermissionGuard>
      }
    />

    {/* operator */}
    <Route
      path="/operator"
      element={<Navigate to="/operator/tenant-payments" replace />}
    />
    <Route
      path="/operator/tenant-payments"
      element={wrap(OperatorTenantPayments)}
    />
    <Route
      path="/operator/platform-accesses"
      element={wrap(OperatorPlatformAccesses)}
    />
    <Route
      path="/operator/data-reconciliation"
      element={wrap(OperatorDataReconciliation)}
    />
    <Route path="/operator/app-status" element={wrap(OperatorAppStatus)} />
    <Route
      path="/operator/data-migrations"
      element={wrap(OperatorDataMigrations)}
    />

    {/* bookings: イベント申込はイベント詳細へ統合 */}
    <Route path="/bookings" element={<Navigate to="/events" replace />} />
    <Route path="/bookings/:id" element={<BookingRedirectAdapter />} />

    {/* chat */}
    <Route path="/chat" element={wrap(ChatLayout)}>
      <Route index element={wrapChatPanel(Chat)} />
      <Route path=":roomId" element={wrapChatPanel(ChatRoom)} />
    </Route>

    {/* coaches */}
    <Route path="/coaches" element={wrap(Coaches)} />
    <Route path="/coaches/create" element={wrap(CoachCreate)} />

    {/* courses */}
    <Route path="/courses" element={wrap(Courses)} />
    <Route
      path="/courses/create"
      element={
        <StaffPermissionGuard permission="course.manage">
          {wrap(CourseCreate)}
        </StaffPermissionGuard>
      }
    />
    <Route path="/courses/:id" element={wrapCourseScoped(CourseDetail)} />
    <Route path="/courses/:id/edit" element={wrapCourseManaged(CourseEdit)} />
    <Route
      path="/learning-catalog"
      element={
        <StaffPermissionGuard
          permission="course.manage"
          requireGlobalFacilityScope
        >
          {wrap(LearningCatalog)}
        </StaffPermissionGuard>
      }
    />
    <Route
      path="/video-materials"
      element={
        <StaffPermissionGuard
          permission="course.manage"
          requireGlobalFacilityScope
        >
          {wrap(VideoMaterials)}
        </StaffPermissionGuard>
      }
    />
    <Route
      path="/courses/:id/sessions"
      element={wrapCourseScoped(CourseSessions)}
    />
    <Route
      path="/courses/:id/schedule-change"
      element={wrapCourseManaged(CourseScheduleChange)}
    />
    <Route
      path="/courses/:id/sessions/create"
      element={wrapCourseManaged(CourseSessionCreate)}
    />
    <Route
      path="/courses/:id/sessions/:sessionId"
      element={wrapCourseScoped(CourseSessionDetail)}
    />
    <Route
      path="/courses/:id/sessions/:sessionId/lessons/:date"
      element={wrapCourseScoped(LessonOccurrenceDetail)}
    />
    <Route
      path="/courses/:id/sessions/:sessionId/edit"
      element={wrapCourseManaged(CourseSessionEdit)}
    />

    {/* customers */}
    <Route path="/customers/members" element={wrap(CustomerMembers)} />
    <Route
      path="/customers/members/invite"
      element={
        <StaffPermissionGuard permission="member.manage">
          {wrap(CustomerMembersInvite)}
        </StaffPermissionGuard>
      }
    />
    <Route
      path="/customers/members/identity-review"
      element={wrap(CustomerMemberIdentityReview)}
    />
    <Route path="/customers/members/:id" element={wrap(CustomerMemberDetail)} />
    <Route
      path="/customers/members/:id/edit"
      element={
        <StaffPermissionGuard permission="member.manage">
          {wrap(CustomerMemberEdit)}
        </StaffPermissionGuard>
      }
    />
    {/* 既存ブックマークと公開済みスタッフアプリからの旧URLを正準URLへ寄せる。 */}
    <Route
      path="/customers/parents"
      element={<LegacyMemberRouteRedirect destination="list" />}
    />
    <Route
      path="/customers/parents/invite"
      element={<LegacyMemberRouteRedirect destination="invite" />}
    />
    <Route
      path="/customers/parents/identity-review"
      element={<LegacyMemberRouteRedirect destination="identity-review" />}
    />
    <Route
      path="/customers/parents/:id"
      element={<LegacyMemberRouteRedirect destination="detail" />}
    />
    <Route
      path="/customers/parents/:id/edit"
      element={<LegacyMemberRouteRedirect destination="edit" />}
    />
    <Route path="/customers/children" element={wrap(CustomerChildren)} />
    <Route
      path="/customers/children/create"
      element={
        <StaffPermissionGuard permission="member.manage">
          {wrap(CustomerChildCreate)}
        </StaffPermissionGuard>
      }
    />
    <Route
      path="/customers/children/identity-review"
      element={wrap(CustomerChildIdentityReview)}
    />
    <Route path="/customers/children/:id" element={wrap(CustomerChildDetail)} />
    <Route
      path="/customers/children/:id/edit"
      element={
        <StaffPermissionGuard permission="member.manage">
          {wrap(CustomerChildEdit)}
        </StaffPermissionGuard>
      }
    />
    <Route
      path="/customers/payments"
      element={
        <StaffPermissionGuard permission="billing.manage">
          <BillingHistoryRedirectAdapter />
        </StaffPermissionGuard>
      }
    />
    <Route
      path="/customers/import"
      element={
        <StaffPermissionGuard
          permission="bulk_operation.execute"
          requireGlobalFacilityScope
        >
          {wrap(CustomerImport)}
        </StaffPermissionGuard>
      }
    />

    {/* dashboard */}
    <Route path="/dashboard" element={wrap(Dashboard)} />

    {/* events */}
    <Route path="/events" element={wrap(Events)} />
    <Route path="/events/create" element={wrap(EventCreate)} />
    <Route path="/events/:id" element={wrap(EventDetail)} />
    <Route path="/events/:id/edit" element={wrap(EventEdit)} />
    <Route path="/events/:id/participants" element={wrap(EventParticipants)} />

    {/* facilities */}
    <Route path="/facilities" element={wrap(Facilities)} />
    <Route
      path="/facilities/create"
      element={
        <StaffPermissionGuard
          permission="role.manage"
          requireGlobalFacilityScope
        >
          {wrap(FacilityCreate)}
        </StaffPermissionGuard>
      }
    />
    <Route path="/facilities/:id" element={wrap(FacilityDetail)} />
    <Route
      path="/facilities/:id/edit"
      element={
        <StaffPermissionGuard permission="course.manage">
          {wrap(FacilityEdit)}
        </StaffPermissionGuard>
      }
    />
    <Route
      path="/facilities/:id/closed-dates"
      element={wrap(FacilityClosedDates)}
    />
    <Route
      path="/facilities/:id/reschedule-policy"
      element={
        <StaffPermissionGuard permission="role.manage">
          {wrap(FacilityReschedulePolicy)}
        </StaffPermissionGuard>
      }
    />
    <Route
      path="/facilities/:id/membership-policy"
      element={
        <StaffPermissionGuard permission="role.manage">
          {wrap(FacilityMembershipPolicy)}
        </StaffPermissionGuard>
      }
    />
    <Route
      path="/facilities/:id/member-management-mode"
      element={wrap(FacilityMemberManagementMode)}
    />
    <Route
      path="/facilities/:id/ticket-reservations"
      element={wrap(TicketReception)}
    />
    <Route
      path="/facilities/:id/membership-policy/suspension-rules"
      element={
        <StaffPermissionGuard permission="role.manage">
          {wrap(SuspensionRulesPage)}
        </StaffPermissionGuard>
      }
    />
    <Route
      path="/facilities/:id/attendance-setting"
      element={
        <StaffPermissionGuard permission="course.manage">
          {wrap(FacilityAttendanceSetting)}
        </StaffPermissionGuard>
      }
    />
    <Route
      path="/facilities/:id/chat-support-policy"
      element={
        <StaffPermissionGuard permission="role.manage">
          {wrap(FacilityChatSupportPolicy)}
        </StaffPermissionGuard>
      }
    />
    <Route
      path="/facilities/:id/courses"
      element={<FacilityCourseRedirectAdapter destination="list" />}
    />
    <Route
      path="/facilities/:id/courses/create"
      element={
        <StaffPermissionGuard permission="course.manage">
          <FacilityCourseRedirectAdapter destination="create" />
        </StaffPermissionGuard>
      }
    />
    <Route
      path="/facilities/:id/courses/:courseId/edit"
      element={
        <StaffPermissionGuard permission="course.manage">
          <FacilityCourseRedirectAdapter destination="edit" />
        </StaffPermissionGuard>
      }
    />
    <Route
      path="/facilities/:id/instructors"
      element={wrap(FacilityInstructors)}
    />
    <Route
      path="/facilities/:id/instructors/create"
      element={wrap(FacilityInstructorCreate)}
    />
    <Route
      path="/facilities/:id/instructors/:instructorId/edit"
      element={wrap(FacilityInstructorEdit)}
    />

    {/* organization */}
    <Route
      path="/organization/payout-account"
      element={
        <StaffPermissionGuard
          permission="billing.manage"
          requireGlobalFacilityScope
        >
          {wrap(OrganizationPayoutAccount)}
        </StaffPermissionGuard>
      }
    />
    <Route
      path="/organization/branding"
      element={
        <StaffPermissionGuard permission="role.manage">
          {wrap(OrganizationBranding)}
        </StaffPermissionGuard>
      }
    />
    <Route
      path="/organization/reschedule-policy"
      element={
        <StaffPermissionGuard
          permission="role.manage"
          requireGlobalFacilityScope
        >
          {wrap(OrganizationReschedulePolicy)}
        </StaffPermissionGuard>
      }
    />
    <Route
      path="/organization/membership-policy"
      element={
        <StaffPermissionGuard
          permission="role.manage"
          requireGlobalFacilityScope
        >
          {wrap(OrganizationMembershipPolicy)}
        </StaffPermissionGuard>
      }
    />
    <Route
      path="/organization/member-management-mode"
      element={wrap(OrganizationMemberManagementMode)}
    />
    <Route
      path="/organization/membership-policy/suspension-rules"
      element={
        <StaffPermissionGuard
          permission="role.manage"
          requireGlobalFacilityScope
        >
          {wrap(SuspensionRulesPage)}
        </StaffPermissionGuard>
      }
    />
    <Route
      path="/organization/attendance-setting"
      element={
        <StaffPermissionGuard
          permission="role.manage"
          requireGlobalFacilityScope
        >
          {wrap(OrganizationAttendanceSetting)}
        </StaffPermissionGuard>
      }
    />
    <Route
      path="/organization/chat-support-policy"
      element={
        <StaffPermissionGuard
          permission="role.manage"
          requireGlobalFacilityScope
        >
          {wrap(OrganizationChatSupportPolicy)}
        </StaffPermissionGuard>
      }
    />
    <Route path="/facilities/:id/rooms" element={wrap(FacilityRooms)} />
    <Route
      path="/facilities/:id/rooms/create"
      element={
        <StaffPermissionGuard permission="course.manage">
          {wrap(FacilityRoomCreate)}
        </StaffPermissionGuard>
      }
    />
    <Route
      path="/facilities/:id/rooms/:roomId"
      element={
        <StaffPermissionGuard permission="course.manage">
          <RoomDetailRedirectAdapter />
        </StaffPermissionGuard>
      }
    />
    <Route
      path="/facilities/:id/rooms/:roomId/edit"
      element={
        <StaffPermissionGuard permission="course.manage">
          {wrap(FacilityRoomEdit)}
        </StaffPermissionGuard>
      }
    />

    {/* faqs */}
    <Route path="/faqs" element={wrap(Faqs)} />
    <Route
      path="/faqs/create"
      element={
        <StaffPermissionGuard permission="notification.publish">
          {wrap(FaqCreate)}
        </StaffPermissionGuard>
      }
    />
    <Route path="/faqs/:id/edit" element={<FaqEditAdapter />} />

    {/* invitations */}
    <Route path="/invitations" element={wrap(Invitations)} />
    <Route
      path="/invitations/create"
      element={
        <StaffPermissionGuard permission="member.manage">
          {wrap(InvitationCreate)}
        </StaffPermissionGuard>
      }
    />
    <Route path="/invitations/analytics" element={wrap(InvitationsAnalytics)} />

    {/* notifications */}
    <Route
      path="/notifications"
      element={
        <StaffPermissionGuard permission="notification.publish">
          {wrap(Notifications)}
        </StaffPermissionGuard>
      }
    />
    <Route path="/notifications/inbox" element={wrap(NotificationInbox)} />
    <Route
      path="/notifications/create"
      element={
        <StaffPermissionGuard permission="notification.publish">
          {wrap(NotificationCreate)}
        </StaffPermissionGuard>
      }
    />
    <Route
      path="/notifications/:id"
      element={
        <StaffPermissionGuard permission="notification.publish">
          {wrap(NotificationDetail)}
        </StaffPermissionGuard>
      }
    />
    <Route
      path="/notifications/:id/edit"
      element={
        <StaffPermissionGuard permission="notification.publish">
          {wrap(NotificationEdit)}
        </StaffPermissionGuard>
      }
    />
    <Route
      path="/notifications/segments"
      element={
        <StaffPermissionGuard
          permission="notification.publish"
          requireGlobalFacilityScope
        >
          {wrap(NotificationSegments)}
        </StaffPermissionGuard>
      }
    />
    <Route
      path="/notifications/segments/create"
      element={
        <StaffPermissionGuard
          permission="notification.publish"
          requireGlobalFacilityScope
        >
          {wrap(NotificationSegmentCreate)}
        </StaffPermissionGuard>
      }
    />
    <Route
      path="/notifications/segments/:id"
      element={
        <StaffPermissionGuard
          permission="notification.publish"
          requireGlobalFacilityScope
        >
          {wrap(NotificationSegmentDetail)}
        </StaffPermissionGuard>
      }
    />
    <Route
      path="/notifications/segments/:id/edit"
      element={
        <StaffPermissionGuard
          permission="notification.publish"
          requireGlobalFacilityScope
        >
          {wrap(NotificationSegmentEdit)}
        </StaffPermissionGuard>
      }
    />

    {/* attendance-reports */}
    <Route path="/attendance-reports" element={wrap(AttendanceReports)} />

    {/* attendance-board はダッシュボードの週/月タブに統合済み（旧URLはリダイレクト） */}
    <Route
      path="/attendance-board"
      element={<Navigate to="/dashboard" replace />}
    />

    {/* reschedule-requests */}
    <Route path="/reschedule-requests" element={wrap(RescheduleRequests)} />
    <Route
      path="/membership-requests"
      element={
        <StaffPermissionGuard permission="billing.manage">
          {wrap(MembershipRequests)}
        </StaffPermissionGuard>
      }
    />
    <Route
      path="/course-enrollment-requests"
      element={wrap(CourseEnrollmentRequests)}
    />

    {/* settings */}
    <Route path="/settings/account" element={wrap(SettingsAccount)} />
    <Route
      path="/settings/data-import"
      element={
        <StaffPermissionGuard
          permission="bulk_operation.execute"
          requireGlobalFacilityScope
        >
          {wrap(SettingsDataImport)}
        </StaffPermissionGuard>
      }
    />

    {/* staff */}
    <Route
      path="/staff"
      element={
        <StaffPermissionGuard permission="staff.manage">
          {wrap(Staff)}
        </StaffPermissionGuard>
      }
    />
    <Route
      path="/staff/create"
      element={
        <StaffPermissionGuard permission="staff.manage">
          {wrap(StaffCreate)}
        </StaffPermissionGuard>
      }
    />
    <Route
      path="/staff/:id/edit"
      element={
        <StaffPermissionGuard permission="staff.manage">
          {wrap(StaffEdit)}
        </StaffPermissionGuard>
      }
    />
    <Route
      path="/staff/shifts/create"
      element={
        <StaffPermissionGuard permission="staff.manage">
          {wrap(StaffShiftsCreate)}
        </StaffPermissionGuard>
      }
    />
    <Route
      path="/staff/notification-settings"
      element={
        <StaffPermissionGuard
          permissions={["role.manage", "notification.publish"]}
        >
          {wrap(StaffNotificationSettings)}
        </StaffPermissionGuard>
      }
    />

    {/* trial-applications */}
    <Route path="/trial-applications" element={wrap(TrialApplications)} />

    {/* trial-form-templates */}
    <Route
      path="/trial-settings"
      element={
        <StaffPermissionGuard permission="course.manage">
          {wrap(TrialSettings)}
        </StaffPermissionGuard>
      }
    />
    <Route
      path="/trial-form-templates"
      element={
        <StaffPermissionGuard permission="course.manage">
          {wrap(TrialFormTemplates)}
        </StaffPermissionGuard>
      }
    />
    <Route
      path="/trial-form-templates/create"
      element={
        <StaffPermissionGuard permission="course.manage">
          {wrap(TrialFormTemplateCreate)}
        </StaffPermissionGuard>
      }
    />
    <Route
      path="/trial-form-templates/:id/edit"
      element={
        <StaffPermissionGuard permission="course.manage">
          {wrap(TrialFormTemplateEdit)}
        </StaffPermissionGuard>
      }
    />

    {/* products */}
    <Route
      path="/products"
      element={
        <StaffPermissionGuard
          permission="inventory.manage"
          requireGlobalFacilityScope
        >
          {wrap(Products)}
        </StaffPermissionGuard>
      }
    />
    <Route path="/products/inventory" element={wrap(ProductInventory)} />
    <Route
      path="/products/new"
      element={
        <StaffPermissionGuard
          permission="inventory.manage"
          requireGlobalFacilityScope
        >
          {wrap(ProductNew)}
        </StaffPermissionGuard>
      }
    />
    <Route
      path="/products/:productId"
      element={
        <StaffPermissionGuard
          permission="inventory.manage"
          requireGlobalFacilityScope
        >
          {wrap(ProductDetail)}
        </StaffPermissionGuard>
      }
    />
    <Route
      path="/products/:productId/edit"
      element={
        <StaffPermissionGuard
          permission="inventory.manage"
          requireGlobalFacilityScope
        >
          {wrap(ProductEdit)}
        </StaffPermissionGuard>
      }
    />

    {/* surveys */}
    <Route path="/surveys" element={wrap(Surveys)} />
    <Route
      path="/surveys/create"
      element={
        <StaffPermissionGuard permission="notification.publish">
          {wrap(SurveyCreate)}
        </StaffPermissionGuard>
      }
    />
    <Route path="/surveys/:id" element={wrap(SurveyDetail)} />
    <Route
      path="/surveys/:id/edit"
      element={
        <StaffPermissionGuard permission="notification.publish">
          {wrap(SurveyEdit)}
        </StaffPermissionGuard>
      }
    />

    {/* inquiries */}
    <Route
      path="/inquiries"
      element={
        <StaffPermissionGuard permission="inquiry.manage">
          {wrap(Inquiries)}
        </StaffPermissionGuard>
      }
    />

    {/* grade-tests / grade-programs */}
    <Route path="/grade-tests" element={wrap(GradeTests)} />
    <Route path="/grade-programs" element={wrap(GradePrograms)} />
    <Route
      path="/grade-programs/:programId/ranks/:rankId"
      element={wrap(GradeRankDetail)}
    />
    <Route
      path="/grade-programs/:programId/ranks/:rankId/edit"
      element={wrap(GradeRankEdit)}
    />

    {/* orders */}
    <Route path="/orders" element={wrap(Orders)} />
    <Route path="/orders/:id" element={wrap(OrderDetail)} />
  </Route>
);
